import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowRight, Calendar, Tag, PanelLeft, X } from 'lucide-react'
import { blog } from '@/content/site'
import { CalendlyBtn } from '@/components/ui/calendly-btn'

const ALL = 'All'

export default function BlogPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [activeCategory, setActiveCategory] = useState(ALL)

  const categories = useMemo(() => {
    const cats = Array.from(new Set(blog.posts.map(p => p.category).filter(Boolean))) as string[]
    return [ALL, ...cats]
  }, [])

  const filtered = useMemo(() =>
    activeCategory === ALL ? blog.posts : blog.posts.filter(p => p.category === activeCategory),
    [activeCategory]
  )

  function selectCategory(cat: string) {
    setActiveCategory(cat)
    setSidebarOpen(false)
  }

  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen">

      {/* Page hero */}
      <div className="bg-slate-900 pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-blue-400 text-sm font-semibold tracking-widest uppercase mb-3">
            Insights & Perspectives
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="text-4xl sm:text-5xl font-bold text-white mb-4">
            {blog.heading}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="text-slate-400 text-lg max-w-2xl">
            {blog.subheading}
          </motion.p>
        </div>
      </div>

      {/* Mobile sidebar toggle */}
      <div className="lg:hidden sticky top-16 z-30 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 px-4 py-2.5 flex items-center justify-between">
        <span className="text-sm text-slate-500 dark:text-slate-400">
          {activeCategory === ALL ? `All posts (${blog.posts.length})` : `${activeCategory} (${filtered.length})`}
        </span>
        <button
          onClick={() => setSidebarOpen(true)}
          className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        >
          <PanelLeft size={15} />
          Browse
        </button>
      </div>

      {/* Mobile sidebar drawer */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40 lg:hidden"
              onClick={() => setSidebarOpen(false)} />
            <motion.aside
              initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              className="fixed top-0 left-0 h-full w-72 bg-white dark:bg-slate-900 z-50 shadow-2xl flex flex-col lg:hidden"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                <span className="font-semibold text-slate-900 dark:text-white text-sm">Browse Posts</span>
                <button onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                  <X size={16} />
                </button>
              </div>
              <SidebarContent
                categories={categories}
                activeCategory={activeCategory}
                onSelect={selectCategory}
              />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-8">

          {/* Desktop sidebar */}
          <aside className="hidden lg:flex flex-col w-60 flex-shrink-0">
            <div className="sticky top-24">
              <SidebarContent
                categories={categories}
                activeCategory={activeCategory}
                onSelect={selectCategory}
              />
            </div>
          </aside>

          {/* Blog grid */}
          <div className="flex-1 min-w-0">
            {filtered.length === 0 ? (
              <p className="text-slate-400 text-sm py-12 text-center">No posts in this category yet.</p>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((post, i) => (
                  <motion.div
                    key={post.slug}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: (i % 6) * 0.07 }}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="group flex flex-col h-full bg-white dark:bg-slate-800 rounded-2xl overflow-hidden border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1"
                    >
                      {post.image ? (
                        <div className="aspect-[16/9] overflow-hidden bg-slate-100 dark:bg-slate-700">
                          <img src={post.image} alt={post.title} loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        </div>
                      ) : (
                        <div className="aspect-[16/9] bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 flex items-center justify-center">
                          <span className="text-blue-200 text-5xl font-light font-heading">DP</span>
                        </div>
                      )}
                      <div className="flex flex-col flex-1 p-6 gap-3">
                        <div className="flex items-center gap-3 text-xs text-slate-400 dark:text-slate-500">
                          {post.category && (
                            <span className="flex items-center gap-1 text-blue-500 font-semibold uppercase tracking-widest">
                              <Tag size={10} />
                              {post.category}
                            </span>
                          )}
                          {post.date && (
                            <span className="flex items-center gap-1">
                              <Calendar size={10} />
                              {new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                          )}
                        </div>
                        <h2 className="font-semibold text-slate-900 dark:text-white text-lg leading-snug group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed flex-1">
                          {post.excerpt}
                        </p>
                        <span className="inline-flex items-center gap-1.5 text-blue-600 dark:text-blue-400 text-sm font-medium mt-auto pt-2 border-t border-slate-100 dark:border-slate-700">
                          Read Article <ArrowRight size={14} />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CTA strip */}
      <div className="bg-slate-900 py-16 mt-8">
        <div className="max-w-2xl mx-auto text-center px-4">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to do the inner work?</h2>
          <p className="text-slate-400 mb-8">Start with a free conversation. No pressure, no pitch.</p>
          <CalendlyBtn size="lg" className="bg-blue-600 hover:bg-blue-700 text-white border-transparent rounded-full hover:scale-105 hover:shadow-lg hover:shadow-blue-600/25 transition-all">
            Book Free Consultation
          </CalendlyBtn>
        </div>
      </div>
    </div>
  )
}

function SidebarContent({
  categories,
  activeCategory,
  onSelect,
}: {
  categories: string[]
  activeCategory: string
  onSelect: (cat: string) => void
}) {
  return (
    <div className="flex flex-col gap-6 px-4 py-5 overflow-y-auto">

      {/* Category filters */}
      <div>
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">Categories</p>
        <div className="flex flex-col gap-1">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => onSelect(cat)}
              className={`text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === cat
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* All posts list */}
      <div>
        <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">All Posts</p>
        <div className="flex flex-col gap-0.5">
          {blog.posts.map(post => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="px-3 py-2 rounded-lg text-sm text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-blue-600 dark:hover:text-blue-400 transition-colors leading-snug"
            >
              {post.title}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}
